/**
 * Shared UI primitives — buttons, pills, copy fields, banners, etc.
 * Ported from the Claude Design system to typed React components.
 *
 * Styles come from `brand.css` + `dashboard.css`. These components are
 * thin React wrappers that compose the right class names + accessibility
 * attributes; the visual treatment lives entirely in CSS.
 */

import type { ButtonHTMLAttributes, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Ic, type IconName } from "./Icons";

// ─── Button ───────────────────────────────────────────────────────────

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  block?: boolean;
  loading?: boolean;
}

export function Btn({
  variant = "primary",
  block,
  loading,
  className = "",
  children,
  ...rest
}: BtnProps) {
  const cls = [
    "btn",
    `btn-${variant}`,
    block ? "btn-block" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button className={cls} {...rest} disabled={loading || rest.disabled}>
      {loading ? <span className="spin" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

// ─── Status pill ──────────────────────────────────────────────────────

export type StatusKind =
  | "paid"
  | "pending"
  | "expired"
  | "failed"
  | "active"
  | "paused";

const STATUS_META: Record<StatusKind, { cls: string; label: string }> = {
  paid: { cls: "ok", label: "Paid" },
  pending: { cls: "warn", label: "Pending" },
  expired: { cls: "muted", label: "Expired" },
  failed: { cls: "fail", label: "Failed" },
  active: { cls: "ok", label: "Active" },
  paused: { cls: "muted", label: "Paused" },
};

export function Pill({ status }: { status: StatusKind }) {
  const meta = STATUS_META[status];
  return (
    <span className={`pill ${meta.cls}`}>
      <span className="pd" />
      {meta.label}
    </span>
  );
}

// ─── Copy field ───────────────────────────────────────────────────────

export function CopyField({
  value,
  display,
}: {
  value: string;
  display?: string;
}) {
  const [done, setDone] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      // navigator.clipboard not available — the value is still selectable
    }
  };
  return (
    <div className="copyf">
      <div className="val">{display ?? value}</div>
      <button
        className={done ? "copied" : ""}
        onClick={() => void handleCopy()}
        type="button"
        aria-label="Copy to clipboard"
      >
        {done ? (
          <>
            <Ic name="check" size={14} /> Copied
          </>
        ) : (
          <>
            <Ic name="copy" size={14} /> Copy
          </>
        )}
      </button>
    </div>
  );
}

// ─── Toggle switch ────────────────────────────────────────────────────

export function Toggle({
  on,
  onChange,
  ariaLabel,
}: {
  on: boolean;
  onChange: (next: boolean) => void;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      className={`tg-switch${on ? " on" : ""}`}
      onClick={() => onChange(!on)}
    />
  );
}

// ─── Key-value row (settings, detail views) ───────────────────────────

export function KV({
  k,
  mono,
  children,
}: {
  k: string;
  mono?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="kv">
      <span className="k">{k}</span>
      <span className={`v${mono ? " mono" : ""}`}>{children}</span>
    </div>
  );
}

// ─── Banner ───────────────────────────────────────────────────────────

export type BannerTone = "warn" | "ok" | "fail";

export function Banner({
  tone = "warn",
  icon,
  children,
}: {
  tone?: BannerTone;
  icon?: IconName;
  children: ReactNode;
}) {
  const defaultIcon: IconName =
    tone === "ok" ? "check" : tone === "fail" ? "x" : "warn";
  return (
    <div className={`banner ${tone}`}>
      <Ic name={icon ?? defaultIcon} size={16} />
      <div>{children}</div>
    </div>
  );
}

// ─── CountUp — animated number ramp ───────────────────────────────────

export function CountUp({
  value,
  prefix = "$",
  duration = 900,
  decimals = 2,
}: {
  value: number;
  prefix?: string;
  duration?: number;
  decimals?: number;
}) {
  const [current, setCurrent] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setCurrent(value);
      return;
    }
    let start: number | undefined;
    const tick = (t: number) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCurrent(value * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    // Fallback in case the timeline pauses (e.g. unfocused tab).
    const fallback = setTimeout(() => setCurrent(value), duration + 260);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearTimeout(fallback);
    };
  }, [value, duration]);

  return (
    <span className="countup">
      {prefix}
      {current.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
    </span>
  );
}

// ─── Sparkline ────────────────────────────────────────────────────────

export function Sparkline({
  data,
  width = 320,
  height = 46,
}: {
  data: number[];
  width?: number;
  height?: number;
}) {
  if (data.length < 2) {
    return <svg className="spark" viewBox={`0 0 ${width} ${height}`} />;
  }
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const pts: Array<[number, number]> = data.map((d, i) => [
    i * step,
    height - 4 - ((d - min) / range) * (height - 10),
  ]);
  const line = pts
    .map(
      (p, i) =>
        `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`,
    )
    .join(" ");
  const area = `${line} L ${width} ${height} L 0 ${height} Z`;
  const last = pts[pts.length - 1]!;
  return (
    <svg
      className="spark"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="sparkg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--brand-400)" stopOpacity=".28" />
          <stop offset="100%" stopColor="var(--brand-400)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#sparkg)" />
      <path
        d={line}
        fill="none"
        stroke="var(--brand-500)"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={last[0]}
        cy={last[1]}
        r={3.4}
        fill="var(--brand-600)"
        stroke="#fff"
        strokeWidth={1.6}
      />
    </svg>
  );
}

// ─── Chain logo / picker data ─────────────────────────────────────────

export interface ChainInfo {
  id: string;
  name: string;
  blurb: string;
  color: string;
  abbr: string;
  /** LeoKit settlement-asset id for default USDC on this chain. */
  defaultAsset: string;
}

export const CHAINS: ReadonlyArray<ChainInfo> = [
  {
    id: "BASE",
    name: "Base",
    blurb: "Cheap fees, fast. Best default.",
    color: "#0052ff",
    abbr: "B",
    defaultAsset: "BASE.USDC-0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
  },
  {
    id: "ARB",
    name: "Arbitrum",
    blurb: "Cheap fees, fast. Most-used L2.",
    color: "#1b4add",
    abbr: "A",
    defaultAsset: "ARB.USDC-0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
  },
  {
    id: "OP",
    name: "Optimism",
    blurb: "Cheap fees, fast. Mature ecosystem.",
    color: "#ff0420",
    abbr: "O",
    defaultAsset: "OP.USDC-0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85",
  },
  {
    id: "POLY",
    name: "Polygon",
    blurb: "Very cheap fees, fast.",
    color: "#7b3fe4",
    abbr: "P",
    defaultAsset: "POLY.USDC-0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359",
  },
  {
    id: "ETH",
    name: "Ethereum mainnet",
    blurb: "Expensive but most universal.",
    color: "#4a5568",
    abbr: "E",
    defaultAsset: "ETH.USDC-0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
  },
];

export function ChainLogo({ chain, size = 34 }: { chain: string; size?: number }) {
  const c = CHAINS.find((x) => x.id === chain) ?? CHAINS[0]!;
  return (
    <div
      className="chain-logo"
      style={{
        background: c.color,
        width: size,
        height: size,
        fontSize: Math.round(size * 0.4),
      }}
    >
      {c.abbr}
    </div>
  );
}

// ─── Confetti — fires on a celebratory moment ─────────────────────────

export function Confetti({ fire }: { fire: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!fire) return;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = (canvas.width = canvas.offsetWidth);
    const H = (canvas.height = canvas.offsetHeight);
    const colors = ["#08a072", "#1bbd8c", "#5cd6af", "#a6ead2", "#b7791f"];
    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      s: number;
      c: string;
      rot: number;
      vr: number;
      a: number;
    }
    const parts: Particle[] = Array.from({ length: 90 }, () => ({
      x: W / 2 + (Math.random() - 0.5) * 60,
      y: H * 0.32,
      vx: (Math.random() - 0.5) * 9,
      vy: -Math.random() * 12 - 4,
      s: Math.random() * 7 + 4,
      c: colors[Math.floor(Math.random() * colors.length)]!,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      a: 1,
    }));
    let frame = 0;
    let raf: number;
    const loop = () => {
      ctx.clearRect(0, 0, W, H);
      frame++;
      for (const p of parts) {
        p.vy += 0.42;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.vx *= 0.99;
        if (frame > 60) p.a -= 0.018;
        ctx.save();
        ctx.globalAlpha = Math.max(p.a, 0);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 0.6);
        ctx.restore();
      }
      if (frame < 140) raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [fire]);
  if (!fire) return null;
  return <canvas ref={ref} className="confetti" aria-hidden="true" />;
}
